import { router } from '@inertiajs/react'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Plus, Pencil, Trash2, Eye } from 'lucide-react'
import { useState } from 'react'
import NewPageDialog from '@/components/landing-pages/NewPageDialog'

interface SessionSummary {
  id: string
  name: string
  slug: string
  status: string
  view_count: number
  published_url: string | null
  updated_at: string
}

interface TemplateSummary {
  id: string
  name: string
  updated_at: string
}

interface Props {
  sessions: SessionSummary[]
  templates: TemplateSummary[]
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 30) return `${diffDays} days ago`
  return date.toLocaleDateString()
}

function StatusIndicator({ status }: { status: string }) {
  if (status === 'published') {
    return (
      <span className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        <span className="text-xs font-medium text-emerald-600">Published</span>
      </span>
    )
  }
  return (
    <span className="flex items-center gap-2">
      <Pencil className="h-3 w-3 text-amber-500" />
      <span className="text-xs font-medium text-amber-600">Draft</span>
    </span>
  )
}

export default function LandingPagesIndex({ sessions, templates }: Props) {
  const [demoDialogOpen, setDemoDialogOpen] = useState(false)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const [creatingTemplate, setCreatingTemplate] = useState(false)

  const handleCreateTemplate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTemplateName.trim()) return

    setCreatingTemplate(true)
    router.post('/landing_pages/templates', {
      template: {
        name: newTemplateName.trim(),
        product: 'challenge_labs',
      }
    }, {
      onFinish: () => setCreatingTemplate(false),
      onSuccess: () => {
        setTemplateDialogOpen(false)
        setNewTemplateName('')
      }
    })
  }

  return (
    <Layout>
      <div className="p-8">
        {/* Demos Section */}
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Demos</h1>
          <Button variant="outline" onClick={() => setDemoDialogOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            New Demo
          </Button>
        </div>

        {sessions.length === 0 ? (
          <p className="text-sm text-gray-500">No demos yet. Create your first demo to get started.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="grid grid-cols-[100px_1fr_80px_140px_50px] border-b border-gray-200 bg-gray-50 px-5 py-3">
              <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Status</span>
              <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Name</span>
              <span className="text-center text-xs font-medium uppercase tracking-wider text-gray-400">Views</span>
              <span className="text-right text-xs font-medium uppercase tracking-wider text-gray-400">Updated</span>
              <span />
            </div>
            {sessions.map(session => (
              <div
                key={session.id}
                className="grid grid-cols-[100px_1fr_80px_140px_50px] items-center border-b border-gray-200 px-5 py-3.5 transition-colors last:border-b-0 hover:bg-gray-50"
              >
                <StatusIndicator status={session.status} />
                <span
                  className="cursor-pointer text-sm font-medium text-gray-900 hover:text-blue-600"
                  onClick={() => router.visit(`/landing_pages/${session.id}`)}
                >
                  {session.name}
                </span>
                <span className="text-center text-sm text-gray-400">
                  {session.status === 'published' ? (
                    <span className="inline-flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {session.view_count}
                    </span>
                  ) : '—'}
                </span>
                <span className="text-right text-sm text-gray-400">{formatTimeAgo(session.updated_at)}</span>
                <div className="text-right">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (confirm(`Delete "${session.name}"?`)) {
                        router.delete(`/landing_pages/${session.id}`)
                      }
                    }}
                    className="p-1 text-gray-300 transition-colors hover:text-red-500"
                    title="Delete demo"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Templates Section */}
        <div className="mt-10">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Templates</h2>
            <Button variant="outline" size="sm" onClick={() => setTemplateDialogOpen(true)}>
              <Plus className="mr-1 h-3 w-3" />
              New Template
            </Button>
          </div>

          {templates.length === 0 ? (
            <p className="text-sm text-gray-500">No templates yet. Create your first template to get started.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="grid grid-cols-[1fr_140px_140px] border-b border-gray-200 bg-gray-50 px-5 py-3">
                <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Name</span>
                <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Updated</span>
                <span className="text-right text-xs font-medium uppercase tracking-wider text-gray-400">Actions</span>
              </div>
              {templates.map(template => (
                <div
                  key={template.id}
                  className="grid grid-cols-[1fr_140px_140px] items-center border-b border-gray-200 px-5 py-3.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <span className="text-sm font-medium text-gray-900">{template.name}</span>
                  <span className="text-sm text-gray-400">{formatTimeAgo(template.updated_at)}</span>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => router.post(`/landing_pages/${template.id}/edit_template`)}
                    >
                      <Pencil className="mr-1 h-3 w-3" />
                      Edit
                    </Button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete template "${template.name}"?`)) {
                          router.delete(`/landing_pages/templates/${template.id}`)
                        }
                      }}
                      className="p-1 text-gray-300 transition-colors hover:text-red-500"
                      title="Delete template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <NewPageDialog
          open={demoDialogOpen}
          onOpenChange={setDemoDialogOpen}
          templates={templates}
        />

        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Template</DialogTitle>
              <DialogDescription>Create a blank template and customize it with the AI editor.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  placeholder="e.g. Enterprise SaaS Landing"
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={creatingTemplate || !newTemplateName.trim()}>
                  {creatingTemplate ? 'Creating...' : 'Create Template'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  )
}
